import type { UUID } from '@cmg/common';
import { Button, Grid, LoadingButton, Section, SnackbarManager } from '@cmg/design-system';
import { FormikProvider, useFormik } from 'formik';
import { Fragment, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useRoutes } from '../../../../../common/hooks/useRoutes/useRoutes';
import { GQLExtensionErrorAlert } from '../../../../../design-system/alert/GQLExtensionErrorAlert';
import { IdentityPageContent } from '../../../../../design-system/IdentityPageContent';
import { RolodexRecordStatus } from '../../../../../graphql';
import { LocalHighlights } from '../components/LocalHighlights';
import { EmployeeContactInformationFields } from './components/EmployeeContactInformationFields';
import { EmployeeUserDetailsFields } from './components/EmployeeUserDetailsFields';
import { EmployeeValidationAlert } from './components/EmployeeValidationAlert';
import { useCreateUnderwriterEmployee } from './hooks/useCreateUnderwriterEmployee';
import type { EmployeeCreateFormValues } from './models/EmployeeCreateForm.model';
import {
  employeeCreateInitialValues,
  employeeCreateValidationSchema,
} from './models/EmployeeCreateForm.model';

export default function CRMSalesCoverageEmployeeCreateRoute() {
  const navigate = useNavigate();

  const { crmIntegrationId } = useParams<{ crmIntegrationId?: UUID }>();

  const { crmSalesCoverageEmployee, crmSalesCoverage } = useRoutes(crmIntegrationId);

  const redirectUrlToSalesCoverage = useMemo(() => {
    return crmSalesCoverage.getUrlPath({ crmIntegrationId });
  }, [crmIntegrationId, crmSalesCoverage]);

  const { createUnderwriterEmployee, error } = useCreateUnderwriterEmployee(crmIntegrationId);

  const handleSubmit = useCallback(
    async (values: EmployeeCreateFormValues) => {
      try {
        const { data } = await createUnderwriterEmployee({
          input: {
            ...values,
            status: RolodexRecordStatus.Effective,
          },
        });

        const { employeeKey } = data?.createUnderwriterEmployee?.underwriterEmployee ?? {};

        const redirectUrl = employeeKey
          ? crmSalesCoverageEmployee.getUrlPath({ crmIntegrationId, employeeKey })
          : redirectUrlToSalesCoverage;

        navigate(redirectUrl);
        SnackbarManager.success('Employee created successfully.');
      } catch {
        SnackbarManager.error('Failed to create employee.');
      }
    },
    [
      createUnderwriterEmployee,
      crmIntegrationId,
      crmSalesCoverageEmployee,
      navigate,
      redirectUrlToSalesCoverage,
    ]
  );

  const formik = useFormik<EmployeeCreateFormValues>({
    initialValues: employeeCreateInitialValues,
    validationSchema: employeeCreateValidationSchema,
    onSubmit: handleSubmit,
  });

  const handleCancel = useCallback(() => {
    navigate(redirectUrlToSalesCoverage);
  }, [navigate, redirectUrlToSalesCoverage]);

  const handleSave = useCallback(() => {
    formik.handleSubmit();
  }, [formik]);

  const breadcrumbs = useMemo(
    () => [
      {
        label: 'Sales Coverage',
        href: redirectUrlToSalesCoverage,
      },
      { label: 'Create New' },
    ],
    [redirectUrlToSalesCoverage]
  );

  const actions = useMemo(
    () => [
      <Button key="cancel-button" variant="text" onClick={handleCancel}>
        Cancel
      </Button>,
      <LoadingButton
        key="save-button"
        type="submit"
        variant="contained"
        loading={formik.isSubmitting}
        onClick={handleSave}
      >
        Save
      </LoadingButton>,
    ],
    [handleCancel, handleSave, formik.isSubmitting]
  );

  return (
    <FormikProvider value={formik}>
      <IdentityPageContent
        localHighlights={
          <LocalHighlights
            title="Create New Employee"
            breadcrumbs={breadcrumbs}
            actions={actions}
            errors={
              <Fragment>
                <GQLExtensionErrorAlert title="Failed to create employee" error={error} />
                <EmployeeValidationAlert />
              </Fragment>
            }
          />
        }
        gridContent={
          <Fragment>
            <Grid item xs={12} md={6}>
              <Section title="User Details">
                <EmployeeUserDetailsFields autoFocusEmployeeKeyField />
              </Section>
            </Grid>
            <Grid item xs={12} md={6}>
              <Section title="Contact Information">
                <EmployeeContactInformationFields />
              </Section>
            </Grid>
          </Fragment>
        }
      />
    </FormikProvider>
  );
}
